// React is used implicitly for JSX
import { UseFormRegister } from 'react-hook-form';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../contexts/ToastContext';

interface FormTextAreaProps {
  label: string;
  name: string;
  required?: boolean;
  placeholder?: string;
  register: UseFormRegister<any>;
  error?: string;
  maxAttachments?: number;
  currentAttachments?: number;
  onImagePaste?: (file: File) => void;
}

export default function FormTextArea({
  label,
  name,
  required = false,
  placeholder,
  register,
  error,
  onImagePaste,
  maxAttachments = 5,
  currentAttachments = 0
}: FormTextAreaProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const showImgPasteText = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // Show a notification that image was added (optional)
    const textarea = event.currentTarget;
    const cursorPos = textarea.selectionStart;
    const textBefore = textarea.value.substring(0, cursorPos);
    const textAfter = textarea.value.substring(cursorPos);
    const imagePastedText = ` [${t('tickets.imagePasted')}] `;

    // Update the textarea value with a notification text
    textarea.value = textBefore + imagePastedText + textAfter;

    // Move cursor after the inserted text
    textarea.selectionStart = textarea.selectionEnd = cursorPos + imagePastedText.length;

    // Prevent default paste behavior for images
    event.preventDefault();
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (onImagePaste && event.clipboardData) {
      const items = event.clipboardData.items;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          // Get the image file from clipboard
          const file = items[i].getAsFile();
          if (file) {

            // Check if we've reached the maximum number of attachments
            if (currentAttachments >= maxAttachments) {
              showToast(t('tickets.maxAttachmentReached', { max: maxAttachments }), 'error');
              return;
            }
            // Generate a unique filename with timestamp
            const timestamp = new Date().getTime();
            const newFile = new File(
              [file],
              `pasted_image_${timestamp}.${file.name.split('.').pop() || 'png'}`,
              { type: file.type }
            );

            // Call the callback with the new file
            onImagePaste(newFile);

            // Not showing image paste text
            // showImgPasteText(event);

            showToast(t('tickets.imagePasted'), 'success');
            return;
          }
        }
      }
    }
  }

  return (
    <div className="relative">
      <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
        }`}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <textarea
        {...register(name, { required: required && `${label} is required` })}
        rows={4}
        placeholder={placeholder}
        onPaste={handlePaste}
        className={`
          block w-full rounded-md px-3 py-2 text-base
          border border-border
          shadow-sm
          focus:outline-none focus:ring-2 focus:ring-offset-0
          transition-colors duration-200
          resize-vertical
          ${theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
          }
          ${error
            ? theme === 'dark'
              ? 'border-red-500 text-red-200 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
            : theme === 'dark'
              ? 'focus:border-blue-500 focus:ring-blue-500 hover:border-gray-500'
              : 'focus:border-blue-500 focus:ring-blue-500'
          }
          disabled:bg-gray-800 disabled:text-gray-400 disabled:border-gray-700 disabled:cursor-not-allowed
        `}
        aria-invalid={error ? 'true' : 'false'}
      />
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
}